<template>
  <div class="startScreenerView">
    <div class="header">
      <div v-if="avatarDefaultIdleAnimations.length && screenerAvatar" class="avatar-container">
        <AvatarComponent
          :options="{ position: 'dynamic', size: 'height', audio: '' }"
          :inSession="false"
          :isAnimating="true"
        />
      </div>
      <div v-if="!screenerCompleted">
        <h1>
          {{ preloadProgress >= 100 ? (pupilAnswers?.length ? 'Continue' : 'Start') : 'Loading' }}
          <span v-if="isDemoSession" class="demo-title">Demo</span> Screener for
          <span class="fullName">{{ fullName }}</span
          >{{ preloadProgress >= 100 ? '?' : '...' }}
        </h1>
        <DemoWarningComponent v-if="isDemoSession" @exit-demo="reEnterCode" />
      </div>
      <div v-else>
        <LottieAnimation v-if="!screenerAvatar" animationName="complete" width="30%" />
        <h1>
          Screener Completed for
          <span class="fullName">{{ fullName }}</span>
        </h1>
      </div>
    </div>

    <div v-if="preloadProgress >= 100 || screenerCompleted">
      <div v-if="!screenerCompleted && !fullScreenTriggered">
        <div class="buttons">
          <MButton title="Re-enter Code" variant="secondary" @click="reEnterCode" />
          <MButton
            :title="`${pupilAnswers?.length ? 'Continue' : 'Start'} Screener`"
            variant="primary"
            :disabled="!hasMicrophonePermission"
            @click="startScreener"
          />
        </div>

        <p
          v-if="mediaDevicesStore.microphonePermissionRequired"
          class="audioSettings"
          @click="onAudioSettingsClick"
        >
          Audio Settings
        </p>
      </div>

      <div v-else-if="!fullScreenTriggered" class="buttons">
        <MButton
          button-style="width: 15rem;"
          title="Go Back"
          variant="secondary"
          size="lg"
          :icon="faChevronLeft"
          @click="reEnterCode"
          iconPosition="left"
        />
      </div>
      <h2 v-else class="screener-starting">Starting...</h2>
    </div>
    <div v-else class="loading-screen">
      <ProgressBar :progress="preloadProgress" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ComputedRef } from 'vue'
import { computed, ref } from 'vue'
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons'

// Components
import MButton from '@/components/MButton.vue'
import AvatarComponent from '@/components/AvatarComponent.vue'
import ProgressBar from '@/components/ProgressBar.vue'
import LottieAnimation from '@/components/LottieAnimation.vue'
import DemoWarningComponent from '@/components/DemoWarningComponent.vue'

// Stores
import { storeToRefs } from 'pinia'
import { useDataStore } from '@/stores/DataStore'
import { useMediaDevicesStore } from '@/stores/MediaDevicesStore'
import { useEffectsStore } from '@/stores/EffectsStore'
import { usePreloadStore } from '@/stores/PreloadStore'
import { useDevStore } from '@/stores/DevStore'

// Services
import FullScreen from '@/services/FullScreen.js'

// Helpers
import { useTimeout } from '@/utils/helpers'

const emit = defineEmits<{
  (e: 'start-screener'): void
  (e: 're-enter-code'): void
}>()

const { setManagedTimeout } = useTimeout()
const preloadStore = usePreloadStore()
const { preloadProgress } = storeToRefs(preloadStore)
const effectsStore = useEffectsStore()
const dataStore = useDataStore()
const { screenerCompleted, pupilAnswers, screenerAvatar } = storeToRefs(dataStore)
const { avatarDefaultIdleAnimations } = storeToRefs(effectsStore)
const mediaDevicesStore = useMediaDevicesStore()
const devStore = useDevStore()
const { isDemoSession } = storeToRefs(devStore)
const fullscreenChangeDuration = 2000
const fullScreenTriggered = ref(false)

const hasMicrophonePermission: ComputedRef<boolean> = computed(() => {
  return mediaDevicesStore.microphonePermissionRequired
    ? mediaDevicesStore.microphonePermission
    : true
})

const fullName: ComputedRef<string> = computed(() => {
  return dataStore.screenerData?.fullName || ''
})

function onAudioSettingsClick() {
  mediaDevicesStore.setRequiredAudioDevicePrompt(true, false)
}

const reEnterCode = () => {
  emit('re-enter-code')
}

const startScreener = () => {
  fullScreenTriggered.value = true
  if (!FullScreen.isEnabled()) FullScreen.toggleAll()

  setManagedTimeout(() => {
    emit('start-screener')
  }, fullscreenChangeDuration)
}
</script>

<style lang="scss">
.startScreenerView {
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: left;

  .header {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0;
    gap: 2rem;
    max-width: calc(var(--full-vw) * 0.75);

    h1 {
      text-align: center;
    }
    .fullName {
      color: $primary;
    }
  }

  .buttons {
    display: flex;
    gap: 2rem;
    margin-top: 5rem;
  }

  .audioSettings {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 1rem;
    color: $primary;
    text-decoration: underline;
    cursor: pointer;
  }

  .loading-screen {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    margin-top: 4rem;
  }

  .avatar-container {
    height: calc(var(--full-vh) * 0.4);
  }

  .screener-starting {
    color: $secondary;
  }

  .demo-title {
    color: $danger;
  }
}
</style>
